if (!window.Heltra) {
    window.Heltra = {};
}

/**
 * Formats a money value based on the provided format.
 *
 * The function takes a value in cents and a format string, and returns a string representing the money value in the specified format.
 * The format string can contain placeholders in the format `{{ placeholder }}`, where `placeholder` can be one of the following:
 * - 'amount': The money value will be formatted with 2 decimal places.
 * - 'amount_no_decimals': The money value will be formatted with no decimal places.
 * - 'amount_with_comma_separator': The money value will be formatted with 2 decimal places and a comma as the thousand separator.
 * - 'amount_no_decimals_with_comma_separator': The money value will be formatted with no decimal places and a comma as the thousand separator.
 *
 * @param {number|string} cents - The money value in cents. If a string is provided, it should be convertible to a number.
 * @param {string} [format=window.theme.moneyFormat] - The format to use. Defaults to `window.theme.moneyFormat`.
 * @returns {string} - The formatted money value.
 */
Heltra.formatMoney = (cents, format = window.theme.moneyFormat) => {
    if (typeof cents == 'string') {
        cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) {
            return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.'),
            dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`),
            cents = parts[1] ? decimal + parts[1] : '';

        return dollars + cents;
    }

    switch (format.match(placeholderRegex)[1]) {
        case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
        case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
        case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
        case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
    }

    return format.replace(placeholderRegex, value);
};

Heltra.colorUtils = {
    /**
     * Gets the color of the middle pixel of an image.
     *
     * @param {string} imgUrl - The image URL.
     * @returns {Promise<Object>} A promise that resolves to a color object containing the color of the middle pixel
     */
    getMiddlePixelColor: async function getMiddlePixelColor(imageSource) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = imageSource;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const context = canvas.getContext('2d');
                context.drawImage(img, 0, 0, img.width, img.height);
                const middleX = Math.floor(img.width / 2);
                const middleY = Math.floor(img.height / 2);
                const [r, g, b] = context.getImageData(middleX, middleY, 1, 1).data;
                resolve([r, g, b]);
            };
            img.onerror = reject;
        });
    },

    /**
     * Converts RGB values to a color object with various color representations.
     *
     * @param {number} r - The red component of the color, between 0 and 255.
     * @param {number} g - The green component of the color, between 0 and 255.
     * @param {number} b - The blue component of the color, between 0 and 255.
     * @returns {Object} An object containing the original RGB values, an RGB array, a hex string, an HSL array, individual HSL values, and a boolean indicating whether the color is light or dark.
     */
    getColorObject: function getColorObject(r, g, b) {
        const hexString = this.rgbToHex(r, g, b);
        const [hue, saturation, lightness] = this.rgbToHsl(r, g, b);
        return {
            r,
            g,
            b,
            rgb: [r, g, b],
            hexString,
            hsl: [hue, saturation, lightness],
            hue,
            saturation,
            lightness,
            invertUi: lightness < 50
        };
    },

    /**
     * Converts RGB values to a hexadecimal color string.
     *
     * @param {number} r - The red component, between 0 and 255.
     * @param {number} g - The green component, between 0 and 255.
     * @param {number} b - The blue component, between 0 and 255.
     * @returns {string} The hexadecimal color string.
     */
    rgbToHex: function rgbToHex(r, g, b) {
        return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    },

    /**
     * Converts RGB values to HSL.
     *
     * @param {number} r - The red component, between 0 and 255.
     * @param {number} g - The green component, between 0 and 255.
     * @param {number} b - The blue component, between 0 and 255.
     * @returns {Array<number>} The HSL representation [h, s, l].
     */
    rgbToHsl: function rgbToHsl(r, g, b) {
        (r /= 255), (g /= 255), (b /= 255);
        let max = Math.max(r, g, b),
            min = Math.min(r, g, b);
        let h,
            s,
            l = (max + min) / 2;

        if (max === min) {
            h = s = 0; // achromatic
        } else {
            let d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }

        return [parseFloat((h * 360).toFixed(2)), parseFloat((s * 100).toFixed(2)), parseFloat((l * 100).toFixed(2))];
    }
};

/**
 * Populates a template with variables.
 *
 * The variables in the template should be in the format `{# variable #}`.
 * For example, if you have a variable named `foo`, you would use it in the template like this: `{# foo #}`.
 *
 * @param {HTMLTemplateElement} template - The template to populate.
 * @param {Object} variables - An object containing the variables to populate the template with.
 * @returns {DocumentFragment} - The populated template as a DocumentFragment.
 */
Heltra.populateTemplate = (template, variables) => {
    if (!template || !(template instanceof HTMLTemplateElement)) {
        throw new Error('Invalid template element');
    }
    if (typeof variables !== 'object' || variables === null) {
        throw new Error('Invalid variables object');
    }

    const tempDiv = document.createElement('div');
    tempDiv.appendChild(template.content.cloneNode(true));

    tempDiv.innerHTML = tempDiv.innerHTML.replace(/{#\s*(.*?)\s*#}/g, (match, variableName) => variables[variableName] || '');

    const clone = document.createDocumentFragment();
    while (tempDiv.firstChild) {
        clone.appendChild(tempDiv.firstChild);
    }

    return clone;
};
